import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useMutation } from "@tanstack/react-query";
import { Form, Message } from "semantic-ui-react";
import { shipmentMethodsAtom } from "../state";
import {
  shipmentMethodOptionsAtom,
  shipmentMethodOptionsCountryAtom,
} from "../atoms/shipmentMethodOptionsAtom";
import { userAtom } from "../atoms/authAtom";

const removeShipment = async (shipmentId) => {
  const response = await fetch(`/api/shipments/${shipmentId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const AdminOrderStatus = ({ order, shipments, mutation, picked, onShipmentChanged = () => Promise.resolve() }) => {
  const [shipmentMethods] = useAtom(shipmentMethodsAtom);
  const [shipmentMethodOptions] = useAtom(shipmentMethodOptionsAtom);
  const [, setShipmentMethodOptionsCountry] = useAtom(
    shipmentMethodOptionsCountryAtom,
  );
  const [user] = useAtom(userAtom);

  const [everythingPicked, setEverythingPicked] = useState(false);
  const [removeShipmentConfirmation, setRemoveShipmentConfirmation] =
    useState(false);
  const [shipmentMethodId, setShipmentMethodId] = useState(order?.shipment_method_id || 0);
  const [trackingReference, setTrackingReference] = useState("");

  const removeShipmentMutation = useMutation({
    mutationFn: removeShipment,
    onSuccess: async () => {
      await onShipmentChanged();
      setRemoveShipmentConfirmation(false);
    },
  });

  useEffect(() => {
    setShipmentMethodId(order?.shipment_method_id || 0);
  }, [order?.shipment_method_id]);

  useEffect(() => {
    const pickedQuantity = Object.values(picked).reduce((total, quantity) => total + quantity, 0);
    const orderQuantity = (order?.order_rows || []).reduce((total, item) => total + item.quantity, 0);

    setEverythingPicked(orderQuantity > 0 && pickedQuantity === orderQuantity);
    setShipmentMethodOptionsCountry(order?.country_id);
  }, [order, order?.order_rows, picked, setShipmentMethodOptionsCountry]);

  if (!user?.admin) {
    return <></>;
  }

  const shipmentValues = Object.values(shipments || {});
  const selectedShipmentMethod = shipmentMethods[shipmentMethodId];
  const shipmentPending = mutation.isPending;
  const removeShipmentPending = removeShipmentMutation.isPending;

  if (shipmentValues.length === 0) {
    return (
      <>
        <Message>
          <Form>
            <Form.Select
              label="Shipment Method"
              options={shipmentMethodOptions}
              value={shipmentMethodId}
              placeholder="Select Shipment Method"
              disabled={shipmentPending}
              onChange={(e, { value }) => setShipmentMethodId(value)}
            />
            <Form.Input
              label="Tracking Number"
              placeholder="Tracking Number"
              value={trackingReference}
              onChange={(e, { value }) => setTrackingReference(value)}
              required={selectedShipmentMethod?.tracking_required}
              disabled={shipmentPending || !selectedShipmentMethod?.tracking_required}
            />
            <Form.Button
              type="button"
              content="Ship"
              icon="shipping fast"
              labelPosition="left"
              color="green"
              loading={shipmentPending}
              disabled={
                shipmentPending ||
                shipmentMethodId === 0 ||
                (selectedShipmentMethod?.tracking_required && trackingReference === "") ||
                !everythingPicked
              }
              onClick={() => {
                mutation.mutate({
                  orderId: order.id,
                  data: {
                    order_id: order.id,
                    shipment_method_id: shipmentMethodId,
                    tracking_reference: trackingReference,
                  },
                });
              }}
            />
          </Form>
        </Message>
      </>
    );
  }

  return (
    <Message>
      <br />
      <Form>
        <Form.Checkbox
          checked={removeShipmentConfirmation}
          disabled={removeShipmentPending}
          label="Remove Shipment"
          onChange={() => setRemoveShipmentConfirmation(!removeShipmentConfirmation)}
        />
        <Form.Button
          type="button"
          content="Remove"
          disabled={!removeShipmentConfirmation || removeShipmentPending}
          icon="trash"
          labelPosition="left"
          color="red"
          loading={removeShipmentPending}
          onClick={() => {
            removeShipmentMutation.mutate(shipmentValues[0].id);
          }}
        />
      </Form>
    </Message>
  );
};

export default React.memo(AdminOrderStatus);
